'use client';

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Globe } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";

interface HeroSectionProps {
    locale: string;
}

export default function HeroSection({ locale }: HeroSectionProps) {
    const t = useTranslations('mdnqontak');

    return (
        <section className="relative min-h-screen flex items-center justify-center pt-20 md:pt-24 lg:pt-0 overflow-hidden">
            {/* Enhanced Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
                {/* Overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-indigo-900/20"></div>
            </div>

            {/* Enhanced Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Primary floating orbs */}
                <div className="absolute top-1/4 left-1/4 w-32 md:w-64 h-32 md:h-64 bg-gradient-to-br from-blue-200/40 to-purple-200/40 dark:from-blue-900/40 dark:to-purple-900/40 rounded-full opacity-60 animate-pulse blur-xl"></div>
                <div className="absolute top-3/4 right-1/4 w-24 md:w-48 h-24 md:h-48 bg-gradient-to-br from-purple-200/40 to-pink-200/40 dark:from-purple-900/40 dark:to-pink-900/40 rounded-full opacity-60 animate-pulse delay-1000 blur-lg"></div>
                <div className="absolute bottom-1/4 left-1/3 w-16 md:w-32 h-16 md:h-32 bg-gradient-to-br from-green-200/40 to-blue-200/40 dark:from-green-900/40 dark:to-blue-900/40 rounded-full opacity-60 animate-pulse delay-500 blur-md"></div>
                
                {/* Additional floating elements */}
                <div className="absolute top-1/2 right-1/3 w-20 md:w-40 h-20 md:h-40 bg-gradient-to-br from-indigo-200/30 to-blue-200/30 dark:from-indigo-900/30 dark:to-blue-900/30 rounded-full opacity-50 animate-pulse delay-2000 blur-lg"></div>
                <div className="absolute bottom-1/3 right-1/4 w-12 md:w-24 h-12 md:h-24 bg-gradient-to-br from-yellow-200/30 to-orange-200/30 dark:from-yellow-900/30 dark:to-orange-900/30 rounded-full opacity-50 animate-pulse delay-1500 blur-md"></div>

                {/* Geometric patterns */}
                <div className="absolute inset-0 opacity-5 dark:opacity-10">
                    <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="heroGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                            </pattern>
                            <pattern id="heroDots" width="20" height="20" patternUnits="userSpaceOnUse">
                                <circle cx="10" cy="10" r="1" fill="currentColor" opacity="0.3"/>
                            </pattern>
                        </defs>
                        <rect width="100" height="100" fill="url(#heroGrid)" className="text-blue-500 dark:text-blue-400" />
                        <rect width="100" height="100" fill="url(#heroDots)" className="text-purple-500 dark:text-purple-400" />
                    </svg>
                </div>
            </div>

            {/* Enhanced Multi-layered Wave SVG */}
            <div className="absolute bottom-0 left-0 w-full">
                {/* Background wave layer */}
                <svg
                    className="absolute bottom-0 w-full h-20 md:h-32 text-blue-100/50 dark:text-slate-700/30"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        fill="currentColor"
                        d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                        className="animate-pulse"
                        opacity="0.4"
                    />
                </svg>

                {/* Middle wave layer */}
                <svg
                    className="absolute bottom-0 w-full h-18 md:h-28 text-purple-100/60 dark:text-slate-600/40"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        fill="currentColor"
                        d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                        className="animate-pulse"
                        style={{ animationDelay: '1s' }}
                        opacity="0.6"
                    />
                </svg>

                {/* Front wave layer */}
                <svg
                    className="relative block w-full h-16 md:h-24 text-white dark:text-slate-800"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        fill="currentColor"
                        d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                        className="drop-shadow-lg"
                    />
                </svg>

                {/* Floating particles */}
                <div className="absolute bottom-20 left-1/4 w-2 h-2 bg-blue-400/60 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
                <div className="absolute bottom-16 right-1/3 w-1.5 h-1.5 bg-purple-400/60 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
                <div className="absolute bottom-24 left-1/2 w-1 h-1 bg-green-400/60 rounded-full animate-bounce" style={{ animationDelay: '2s', animationDuration: '3.5s' }}></div>
                <div className="absolute bottom-14 right-1/4 w-1.5 h-1.5 bg-yellow-400/60 rounded-full animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '4.5s' }}></div>
            </div>

            {/* Enhanced Content */}
            <div className="container relative z-10 text-center px-4 py-8 md:py-12">
                <div className="max-w-4xl mx-auto">
                    {/* Enhanced Badge */}
                    <div className="inline-flex items-center mb-6">
                        <Badge className="relative px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <span className="relative z-10">{t('hero.badge')}</span>
                            {/* Badge glow effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 blur-md opacity-50 animate-pulse"></div>
                        </Badge>
                    </div>

                    {/* Enhanced Title with better typography */}
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                        <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                            {t('hero.title')}
                        </span>
                    </h1>

                    {/* Enhanced Subtitle */}
                    <div className="text-xl md:text-2xl lg:text-3xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto">
                        <p className="mb-2">
                            {t('hero.subtitle')}
                        </p>
                        <p className="font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {t('hero.highlight')}
                        </p>
                    </div>

                    {/* Enhanced CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
                        <Link href="https://www.qontak.com/schedule_demo?referral_code=MPABDILL" target="_blank">
                            <Button 
                                size="lg" 
                                className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-lg"
                            >
                                <span className="relative z-10 flex items-center">
                                    {t('hero.ctaPrimary')}
                                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                                </span>
                                {/* Button glow effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                            </Button>
                        </Link>
                        <Link href="#quotation-section">
                            <Button 
                                variant="outline" 
                                size="lg"
                                className="group px-8 py-4 border-2 border-slate-300 hover:border-blue-500 hover:text-blue-500 dark:border-slate-600 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-300 hover:scale-105 text-lg"
                            >
                                <Globe className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                                {t('hero.ctaSecondary')}
                            </Button>
                        </Link>
                    </div>

                    {/* Enhanced Stats with better visual hierarchy */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                        {[
                            { value: "300%", label: t('hero.stats.response'), color: "from-blue-600 to-blue-400" },
                            { value: "40%", label: t('hero.stats.workload'), color: "from-purple-600 to-purple-400" },
                            { value: "10k+", label: t('hero.stats.customers'), color: "from-green-600 to-green-400" },
                            { value: "99.9%", label: t('hero.stats.uptime'), color: "from-orange-600 to-orange-400" }
                        ].map((stat, index) => (
                            <div 
                                key={index}
                                className="group hover:scale-110 transition-transform duration-300 cursor-pointer"
                            >
                                <div className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-1`}>
                                    {stat.value}
                                </div>
                                <div className="text-sm md:text-base text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200 transition-colors">
                                    {stat.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Additional floating elements for depth */}
            <div className="absolute top-20 left-10 w-4 h-4 bg-blue-400/30 rounded-full animate-ping" style={{ animationDuration: '4s' }}></div>
            <div className="absolute top-40 right-20 w-3 h-3 bg-purple-400/30 rounded-full animate-ping" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
            <div className="absolute bottom-40 left-20 w-2 h-2 bg-green-400/30 rounded-full animate-ping" style={{ animationDelay: '1s', animationDuration: '3s' }}></div>
        </section>
    );
}